import React from 'react';
import '../styles/CoLivingPlanTracker.css';

// The floor plan, following the photo grid. Scroll the gallery and this says WHERE you
// are: the room the photos you're looking at were shot in, lit up on the plan.
//
// It's the walkthrough's question asked backwards. The walkthrough (CoLivingFloorPlanWalkthrough)
// starts from the plan — pick a room, see its photos. But most people arrive at a deal by
// scrolling photos, and the thing they can't do is hold the map in their head while they
// do it. This closes that: the plan just keeps up.
//
// Every room is drawn, but only rooms we can actually scroll to are clickable — a room
// with no photo has nowhere to send you, so it stays inert rather than dead-clicking.
//
// A photo the room locator could not place gets NO highlight and says so. It never
// guesses a room to keep the marker moving: a marker on the wrong room is worse than no
// marker, because you'd believe it.

function isDrawable(room, sheets) {
    return !!room
        && room.bbox
        && room.bbox.w > 0 && room.bbox.h > 0
        && Number.isInteger(room.imageIndex)
        && !!sheets[room.imageIndex];
}

export default function CoLivingPlanTracker({ sheets, rooms, activeRoom, roomHasPhotos, onSelectRoom }) {
    const drawable = (rooms || []).filter((r) => isDrawable(r, sheets));
    if (!drawable.length) return null;

    // Show the sheet the active room lives on; fall back to the first sheet that has any
    // room on it, so the panel never renders a plan with nothing to point at.
    const sheetIndex = isDrawable(activeRoom, sheets) ? activeRoom.imageIndex : drawable[0].imageIndex;
    const sheetUrl = sheets[sheetIndex];
    if (!sheetUrl) return null;

    const sheetRooms = drawable.filter((r) => r.imageIndex === sheetIndex);
    const levelName = sheetRooms.find((r) => r.level)?.level || null;

    // A house has three rooms printed "Bedroom" and two printed "Closet", and most plans
    // don't print dimensions to tell them apart — so without this, a screen reader hears
    // the same button three times with no way to distinguish them. (Measured: 8 of the 9
    // real co-living deals that show this panel collide at least once.) Number the repeats
    // in the ACCESSIBLE name only; the visible room name stays the plan's printed label.
    const labelCounts = new Map();
    for (const r of sheetRooms) labelCounts.set(r.label, (labelCounts.get(r.label) || 0) + 1);
    const seenLabels = new Map();
    const accessibleName = (r) => {
        const total = labelCounts.get(r.label) || 1;
        if (total < 2) return r.label;
        const n = (seenLabels.get(r.label) || 0) + 1;
        seenLabels.set(r.label, n);
        return `${r.label} (${n} of ${total})`;
    };

    return (
        <div className="coliving-plantrack" role="group" aria-label="Floor plan — you are here">
            <span className="coliving-plantrack-head label-bold-caps colour-secondary">
                {levelName ? `You are here · ${levelName}` : 'You are here'}
            </span>

            {/* The hotspots are positioned in PERCENTAGES of this box, so it must equal the
                rendered image box exactly or every one of them drifts off its room. That
                rules out letting the box grow or the image shrink inside it: the image
                drives both axes and the box wraps it. Same contract (and same hard-won
                reason) as .coliving-fpwalk-sheet — if you change one, change both. */}
            <div className="coliving-plantrack-sheet">
                <img
                    src={sheetUrl}
                    alt={levelName ? `Floor plan — ${levelName}` : 'Floor plan'}
                    className="coliving-plantrack-img"
                />
                {sheetRooms.map((r) => {
                    const active = activeRoom && r.roomId === activeRoom.roomId;
                    const clickable = !!roomHasPhotos?.(r.roomId);
                    const name = accessibleName(r);
                    return (
                        <button
                            key={r.roomId}
                            type="button"
                            className={`coliving-plantrack-room${active ? ' coliving-plantrack-room--active' : ''}${clickable ? ' coliving-plantrack-room--clickable' : ''}`}
                            style={{
                                left: `${r.bbox.x * 100}%`,
                                top: `${r.bbox.y * 100}%`,
                                width: `${r.bbox.w * 100}%`,
                                height: `${r.bbox.h * 100}%`,
                            }}
                            disabled={!clickable}
                            onClick={clickable ? () => onSelectRoom(r.roomId) : undefined}
                            aria-label={clickable
                                ? `${name} — jump to its photos`
                                : `${name} — no photos`}
                            aria-current={active ? 'true' : undefined}
                        />
                    );
                })}
            </div>

            {/* Name the room in words. The highlight says where; this says what — and when a
                photo isn't placed, it says that instead of quietly pointing somewhere. */}
            <span className="coliving-plantrack-room-name body-semibold colour-primary">
                {activeRoom ? activeRoom.label : 'Not placed on the plan'}
            </span>
            {!activeRoom && (
                <span className="coliving-plantrack-note label-regular colour-secondary">
                    We couldn’t tie this photo to one room.
                </span>
            )}
        </div>
    );
}
